// Combining a signed document with its certificate of completion, in
// the browser — same reasoning as pdf-package.ts's split: the bytes are
// already reachable through fetchDocumentBytes, and a Worker isolate
// has no business holding two PDFs in memory just to staple them
// together for one person's one click.
//
// Nate Swartz, 2026-09-20: "I nearly always send the non certified
// document" — plain Download stays a single click at the sealed PDF's
// own server URL (DocumentsTab's existing downloadHref). This module is
// only the SECOND option, "Download with certificate," built on demand.
//
// Lazy-loaded on purpose, same as pdf-package.ts: pdf-lib is already a
// project dependency, but most rows never need this merge.

import { withChunkReload } from "./lazy-with-reload";

/**
 * A merge failure with something a human can act on, same shape as
 * pdf-package.ts's PackageSplitError — the raw pdf-lib message names no
 * next step for the person reading it.
 */
export class CertificateMergeError extends Error {
  readonly userMessage: string;

  constructor(userMessage: string, options?: { cause?: unknown }) {
    super(userMessage, options);
    this.name = "CertificateMergeError";
    this.userMessage = userMessage;
  }
}

/**
 * Appends the certificate's pages onto the end of the signed document
 * and returns one PDF, ready to hand the browser as a download. Both
 * inputs are always PDFs by construction — the archival rail
 * (shared/db/esign-archive.ts) files the sealed copy and the
 * certificate with contentType defaulted to application/pdf, always.
 */
export async function mergeSignedDocumentWithCertificate(
  signedBytes: ArrayBuffer,
  certificateBytes: ArrayBuffer,
): Promise<Blob> {
  // Wrapped so a deploy that landed while this tab was open reloads
  // once instead of surfacing "Failed to fetch dynamically imported
  // module" — see web/src/lib/lazy-with-reload.ts.
  const { PDFDocument } = await withChunkReload(() => import("pdf-lib"));

  let signed: import("pdf-lib").PDFDocument;
  let certificate: import("pdf-lib").PDFDocument;
  try {
    signed = await PDFDocument.load(signedBytes, { ignoreEncryption: true });
    certificate = await PDFDocument.load(certificateBytes, { ignoreEncryption: true });
  } catch (err) {
    throw new CertificateMergeError(
      "Couldn't read one of the files to combine them. Try the plain download instead.",
      { cause: err },
    );
  }

  const pages = await signed.copyPages(certificate, certificate.getPageIndices());
  for (const page of pages) signed.addPage(page);

  const bytes = await signed.save();
  // Same exactly-sized-buffer dance the server-side auto-split uses
  // (shared/db/esign-archive.ts): a fresh pdf-lib save() is already
  // tightly sized, but never assume it — a byteOffset/length mismatch
  // would silently truncate or pad the merged file.
  const body: ArrayBuffer =
    bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
      ? (bytes.buffer as ArrayBuffer)
      : (bytes.slice().buffer as ArrayBuffer);
  return new Blob([body], { type: "application/pdf" });
}
